// libs.
import { useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';

// endpoints.
import { getApproveTaskList } from '../../../../domains/approver-review/slices/approver-task.slice';

// hooks.
import { selectUser } from '../../../../redux/reducers/userSelectors';
import { useAppSelector } from '../../../../redux/hooks';
import { useFetchApproverTasks } from '../../hooks/useFetchApproverTasks';
import { useApproverTaskFilters } from '../../hooks/useApproverTaskFilters';

// components.
import { DataTable } from '../../../../shared/DataTable/DataTable';
import { ProcessType } from './components/ProcessType';
import { statusLabel, UNASSIGNED_AGENT } from '../../utils';
import { PendingTasks } from './components/PendingTasks';
import { DurationCell } from './components/TaskDuration/TaskDuration';
import { SortDataFilter } from '../../../../shared/SortDataFilter/SortDataFilter';
import { ApproverTaskAction } from './components/ApproverTaskAction';
import { MultiSelectDropdown } from '../../../../shared/MultiSelectDropdown/MultiSelectDropdown';

// styles.
import { WrapperTaskTable } from '../styles';

export const ApproverTaskTable = () => {
  const email = useAppSelector(selectUser)?.email;
  const country = localStorage.getItem('country') as 'MX' | 'CO';
  const taskList = useSelector(getApproveTaskList);
  const initializedFor = useRef<string | null>(null);

  const {
    agentOptions,
    filteredData,
    statusOptions,
    sortOrder,
    filters,
    toggleSortOrder,
    handleFilterChange,
  } = useApproverTaskFilters(taskList, country);

  const { isFetching, refetch } = useFetchApproverTasks(
    filters.status.join(','),
    country
  );

  // Preselección: si el usuario es agente aprobador ve solo sus tareas; si no,
  // las ve todas. Se rehace al cambiar de país porque los correos preseleccionados
  // dejan de existir entre las opciones y el filtro quedaría vacío.
  useEffect(() => {
    if (initializedFor.current === country || agentOptions.length === 0) return;

    const allAgentValues = agentOptions.map(opt => opt.value);
    const isEmailInList = email && allAgentValues.includes(email);

    handleFilterChange('agent', isEmailInList ? [email] : allAgentValues);
    initializedFor.current = country;
  }, [email, country, agentOptions, handleFilterChange]);

  if (isFetching) {
    return (
      <div
        className="d-flex flex-column align-items-center m-auto text-copy fw-bold"
        style={{ fontSize: '14px' }}
      >
        <div className="loading-spinner" />
        Cargando tareas...
      </div>
    );
  }

  return (
    <WrapperTaskTable className="py-4 pe-4">
      <PendingTasks
        tasks={taskList}
        refetch={refetch}
        isFetching={isFetching}
      />
      <div className="table-scroll">
        <DataTable
          headers={[
            '#',
            {
              label: 'Fecha',
              filter: (
                <SortDataFilter order={sortOrder} onToggle={toggleSortOrder} />
              ),
            },
            'SLA',
            'NID',
            'Proceso',
            {
              label: 'Agente',
              filter: (
                <MultiSelectDropdown
                  options={agentOptions}
                  value={filters.agent}
                  onChange={selected => handleFilterChange('agent', selected)}
                />
              ),
            },
            {
              label: 'Estado',
              filter: (
                <MultiSelectDropdown
                  value={filters.status}
                  options={statusOptions}
                  onChange={selected => handleFilterChange('status', selected)}
                />
              ),
            },
            '',
          ]}
          data={filteredData}
          renderRow={(row, index) => (
            <tr key={row.id}>
              <td>{index + 1}</td>
              <td>{row.date}</td>
              <td>
                <DurationCell
                  startAt={row.date}
                  updatedAt={row.updated_at}
                  country={country}
                  action={row.status}
                />
              </td>
              <td>{row.nid}</td>
              <td>
                <ProcessType processType={row.process_type} />
              </td>
              <td>{row.agent || UNASSIGNED_AGENT}</td>
              <td>{statusLabel[row.status]}</td>
              <td className="actions-cell">
                <ApproverTaskAction approverTask={row} />
              </td>
            </tr>
          )}
          emptyMessage="No hay tareas que coincidan con este filtro."
        />
      </div>
    </WrapperTaskTable>
  );
};
